import React, { useEffect, useState, useCallback, useRef } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import Beats from './Beats';
import Game from './Game';
import PerformanceManager from './utils/performanceUtils';
import { truncateToDecimal, inMultiplier } from './utils/helpers';
import { useAudio } from './hooks/useAudio';
import DialogModal from './components/DialogModal';
  

function App() {
  const isDev = false;
  const showResetStats = false;
  const [showWelcomeScreen, setShowWelcomeScreen] = React.useState(() => {
    try {
      const storedValue = localStorage.getItem('showWelcomeScreen');
      return storedValue !== null ? storedValue === 'true' : true;
    } catch (error) {
      console.error('Error loading showWelcomeScreen from localStorage:', error);
      return true;
    }
  });
  const [showConfirmResetStats, setShowConfirmResetStats] = React.useState(null);
  const [wonAmount, setWonAmount] = React.useState(0);
  const [wonAmountInFloor, setWonAmountInFloor] = React.useState(0);
  const [wonAmountOutFloor, setWonAmountOutFloor] = React.useState(0);
  const [flagToTriggerHandleHit, setFlagToTriggerHandleHit] = React.useState(false);
  const [isInFloor, setIsInFloor] = React.useState(false);
  const [isGameActive, setIsGameActive] = useState(false);
  const countdownInitial = isDev ? 22 : 45
  const [countdown, setCountdown] = useState(countdownInitial);
  const [isFemale, setIsFemale] = useState(() => {
    try {
      const storedValue = localStorage.getItem('isFemale');
      return storedValue !== null ? storedValue === 'true' : false;
    } catch (error) {
      console.error('Error loading isFemale from localStorage:', error);
      return false;
    }
  });
  const [showTutorial, setShowTutorial] = useState(() => {
    try {
      const storedValue = localStorage.getItem('showTutorial');
      return storedValue !== null ? storedValue === 'true' : (isDev ? false : true);
    } catch (error) {
      console.error('Error loading showTutorial from localStorage:', error);
      return isDev ? false : true;
    }
  });
  const [tutorialStep, setTutorialStep] = useState(() => {
    try {
      const storedValue = localStorage.getItem('tutorialStep');
      return storedValue !== null ? parseInt(storedValue, 10) : 0;
    } catch (error) {
      console.error('Error loading tutorialStep from localStorage:', error);
      return 0;
    }
  });
  const [tutorialDanceCount, setTutorialDanceCount] = useState(8);

  const [cornerIndexCurrent, setCornerIndexCurrent] = useState(() => {
    try {
      // Get the tutorialStep value from localStorage
      const storedTutorialStep = localStorage.getItem('tutorialStep');
      const tutorialStepValue = storedTutorialStep !== null ? parseInt(storedTutorialStep, 10) : 0;
      
      // If in dev mode, or tutorialStep is not 0, set to 2
      if (isDev || tutorialStepValue === 1) {
        return 2;
      } else if (tutorialStepValue !== 0) {
        return 3;
      }
      
      // Default fallback value
      return -1;
    } catch (error) {
      console.error('Error determining initial cornerIndexCurrent:', error);
      return isDev ? 2 : -1; // Fallback to original logic
    }
  });
  const [cornerIndexNext, setCornerIndexNext] = useState(0);
  
  const [performanceSettings, setPerformanceSettings] = useState(PerformanceManager.getFeatureSettings());

  const [isPreGameCountdown, setIsPreGameCountdown] = useState(false);
  const preGameSeconds = isDev ? 1 : 4
  const [preGameCount, setPreGameCount] = useState(preGameSeconds);


  const [showDialog, setShowDialog] = useState(() => {
    try {
      // Check if showWelcomeScreen exists in localStorage
      const storedWelcomeScreen = localStorage.getItem('showWelcomeScreen');
      
      // If showWelcomeScreen doesn't exist in localStorage (first time visitor)
      // or if showWelcomeScreen is true, then show the dialog
      return storedWelcomeScreen === null || storedWelcomeScreen === 'true';
    } catch (error) {
      console.error('Error determining initial dialog state:', error);
      return true; // Default to showing dialog on error
    }
  });
  const [songScore, setSongScore] = useState(0);
  const [amountOfSongs, setAmountOfSongs] = useState(() => {
    try {
      const storedAmount = localStorage.getItem('amountOfSongs');
      return storedAmount ? parseInt(storedAmount, 10) : 0;
    } catch (error) {
      console.error('Error loading amountOfSongs from localStorage:', error);
      return 0;
    }
  });
  const [songScoreAvg, setSongScoreAvg] = useState(() => {
    try {
      const storedAvg = localStorage.getItem('songScoreAvg');
      return storedAvg ? parseFloat(storedAvg) : 0;
    } catch (error) {
      console.error('Error loading songScoreAvg from localStorage:', error);
      return 0;
    }
  });
  const [prevSongScoreAvg, setPrevSongScoreAvg] = useState(() => {
    try {
      const storedPrevAvg = localStorage.getItem('prevSongScoreAvg');
      return storedPrevAvg ? parseFloat(storedPrevAvg) : 0;
    } catch (error) {
      console.error('Error loading prevSongScoreAvg from localStorage:', error);
      return 0;
    }
  });


  // Use our custom audio hook
  const { initializeOrPlay, startPreGameMusic, stopPreGameMusic, isMusicOn, toggleMusic } = useAudio();

  // Add this new state for dialog content type
  const [dialogContent, setDialogContent] = useState('settings'); // 'finish', 'settings', etc.

  // Add a new state to track when angle toggle should happen
  const [toggleAngleFlag, setToggleAngleFlag] = useState(false);

  // Function to render different dialog content based on type
  const renderDialogContent = () => {
    switch (dialogContent) {
      case 'finish':
        return (
          <>
            <p>Danced beat bars</p>
            <div className="finish-row finish-row-scores">
              <div className="f-cell">
                <b>OUT floor</b>
                <span>{wonAmountOutFloor}</span>
              </div>
              <div className="f-cell f-cell-in">
                <b><strong>IN</strong> floor</b>
                <span>{wonAmountInFloor}</span>
              </div>
              <div className="f-cell">
                <b>Score</b>
                <span>{songScore}</span>
              </div>
            </div>
            <div className="finish-divider"></div>
            <div className="finish-row">
              <div className="f-cell f-avg">
                <b>New Average Score</b>
                <span>
                  <span>{prevSongScoreAvg > 0 ? prevSongScoreAvg.toFixed(1) : ''}</span>
                  {songScoreAvg.toFixed(1)}
                </span>
              </div>
            </div>
            {showResetStats && amountOfSongs > 1 && (
              <div className="finish-row">
                <button
                  className="d-button reset-stats-button"
                  onClick={resetStats}
                >
                  <span>Reset Stats</span>
                </button>
              </div>
            )}
          </>
        );


      case 'settings':
        return (
          <>
            <div className={`settings-content ${showWelcomeScreen ? 'is-welcome' : ''}`}>

                <button
                  className={`character-toggle body-button settings-toggle d-button d-small-button ${isFemale ? 'is-female' : ''}`}
                  onClick={() => setIsFemale(!isFemale)}
                >
                  <span>👧</span>
                </button>

                <button
                  className={`music-toggle settings-toggle d-button d-small-button`}
                  onClick={() => {
                    toggleMusic()
                  }}
                >
                  <span>{isMusicOn ? 'On' : 'Off'}</span>
                </button>

                {!showWelcomeScreen &&
                    <>
                        <button
                          className={`guide-toggle settings-toggle d-button d-small-button`}
                          onClick={() => {
                            initializeOrPlay()
                            toggleMusic()
                          }}
                          style={{opacity:  0}}
                        >
                          <span>👁️</span>
                        </button>
                        <button
                          className={`data-toggle settings-toggle d-button d-small-button`}
                          onClick={() => {
                            if(showConfirmResetStats){
                              resetStats();
                              setShowConfirmResetStats(<i>Saved data deleted.</i>)
                            } else {
                              setShowConfirmResetStats(<i>Press again to delete saved data.<br/>This can't be undone.</i>)
                            }
                          }}
                        >

                          <span><i className="confirm-reset">{showConfirmResetStats}</i>🗑️</span>
                        </button>
                    </>
                }
            </div>
          </>
        );

      default:
        return <p>No content available</p>;
    }
  };

  // Function to get dialog title based on content type
  const getDialogTitle = () => {
    switch (dialogContent) {
      case 'finish':
        return <>Song <span>#{amountOfSongs}</span> Finished</>;
      case 'settings':
        return showWelcomeScreen ?  <span>Welcome to <img src="logo.svg" className="welcome-logo" alt="logo" /> <br /> <small className="slogan">Dance to the beat on the DJ's floor</small></span> : 'Settings';
      default:
        return '';
    }
  };

  // Update the triggerHandleHit function to also trigger angle toggle
  const triggerHandleHit = () => {
    // Initialize or play audio
    initializeOrPlay();

    // Toggle angle flag to trigger animation in Player component
    setToggleAngleFlag(prev => !prev);

    // Create a custom event that both Beats.js and Player.js can listen for
    const danceEvent = new CustomEvent('dance-triggered', {
      bubbles: true,
      detail: { source: 'button' }
    });

    window.dispatchEvent(danceEvent);

    // Handle tutorial state
    if (tutorialStep === 0) {
      setTutorialDanceCount(prev => prev - 1);
    }
  };

  const toggleGameActive = () => {
    // If we're stopping the game, just stop it immediately
    if (isGameActive) {
      setIsGameActive(false);
      setPrevSongScoreAvg(songScoreAvg);
      const newScore = wonAmountOutFloor + (wonAmountInFloor * inMultiplier);
      setSongScore(newScore);

      if (amountOfSongs > 0) {
        // Compute the total sum of scores up to now
        const totalSum = songScoreAvg * amountOfSongs;

        // Calculate new sum by adding the new score
        const newSum = totalSum + newScore;

        // Update the count
        const newCount = amountOfSongs + 1;

        // Compute the new average
        const newAverage = newSum / newCount;

        setSongScoreAvg(newAverage);
        setAmountOfSongs(newCount);

        // These will be automatically persisted to localStorage via the useEffect
      } else {
        setSongScoreAvg(newScore);
        setAmountOfSongs(1);
      }

      // Stop pre-game music when game ends
      stopPreGameMusic();

      setDialogContent('finish'); // Set dialog content to finish when game ends
      setShowDialog(true);
      return;
    }

    // If we're starting a new game, show the pre-game countdown first
    if (!isGameActive && !isPreGameCountdown) {
      setTutorialStep(-1)
      // Reset game state
      setWonAmount(0);
      setWonAmountInFloor(0);
      setWonAmountOutFloor(0);
      setCountdown(countdownInitial);

      // Start the pre-game countdown
      setIsPreGameCountdown(true);
      setPreGameCount(preGameSeconds);

      // Start pre-game music with crossfade for the duration of preGameSeconds
      startPreGameMusic(preGameSeconds);
    }
  };

  useEffect(() => {
    let timer;
    if (isGameActive && countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => {
          const newValue = Math.max(0, truncateToDecimal(parseFloat(prev) - 0.1, 1));
          return newValue;
        });
      }, 100); // Update every 100ms for 1 decimal precision
    } else if (isGameActive && countdown <= 0) {
      // Stop the game when countdown reaches zero
      toggleGameActive(); // Call the toggle function to stop the game
    }

    return () => clearInterval(timer); // Cleanup timer on unmount
  }, [isGameActive, countdown]);

useEffect(() => {
  if (wonAmount) {
    if (isInFloor) {
      setWonAmountInFloor(wonAmountInFloor + 1);
    } else {
      setWonAmountOutFloor(wonAmountOutFloor + 1);
    }
  }
}, [wonAmount]);

  useEffect(() => {
    if (showTutorial && isInFloor && tutorialStep === 1) {
      setShowTutorial(false)
      setTutorialStep(2)
    }
  }, [isInFloor]);

  useEffect(() => {
    // Initialize performance detection
    const initialSettings = PerformanceManager.init();
    setPerformanceSettings(initialSettings);

    // Listen for performance changes
    const handlePerformanceChange = (event) => {
      setPerformanceSettings(event.detail);
    };

    window.addEventListener('performanceChange', handlePerformanceChange);

    return () => {
      window.removeEventListener('performanceChange', handlePerformanceChange);
      PerformanceManager.stopPerformanceMonitoring();
    };
  }, []);

  useEffect(() => {
    if (!isPreGameCountdown) return;

    // If count reached 0, start the actual game
    if (preGameCount <= 0) {
      setIsPreGameCountdown(false);
      setIsGameActive(true);
      return;
    }

    // Countdown timer for pre-game
    const timer = setTimeout(() => {
      setPreGameCount(prev => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [isPreGameCountdown, preGameCount]);



  useEffect(() => {
    if (tutorialDanceCount <= 0) {
      setTutorialStep(1);
      setCornerIndexCurrent(2);
    }
  }, [tutorialDanceCount]);

  // Add this useEffect to listen for dance events from any source
  useEffect(() => {
    const handleDanceEvent = () => {
      // Handle tutorial state if needed
      if (tutorialStep === 0) {
        setTutorialDanceCount(prev => prev - 1);
      }
    };

    // Listen for dance events from the spacebar handler
    window.addEventListener('character-danced', handleDanceEvent);

    return () => {
      window.removeEventListener('character-danced', handleDanceEvent);
    };
  }, [tutorialStep]); // Include tutorialStep as a dependency


  const tutorialStepZeroOpacity  = (stepToCheck = 0) => ({
    style: {
      opacity: showTutorial && tutorialStep === stepToCheck ? 0 : 1
    }
  })


  // Persist values to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('amountOfSongs', amountOfSongs.toString());
      localStorage.setItem('songScoreAvg', songScoreAvg.toString());
      localStorage.setItem('prevSongScoreAvg', prevSongScoreAvg.toString());
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }, [amountOfSongs, songScoreAvg, prevSongScoreAvg]);

  // Create a function to reset statistics (useful for adding a reset button)
  const resetStats = useCallback(() => {
    try {
      // Clear existing stats
      localStorage.removeItem('amountOfSongs');
      localStorage.removeItem('songScoreAvg');
      localStorage.removeItem('prevSongScoreAvg');
      
      // Optionally clear other settings too
      localStorage.removeItem('showWelcomeScreen');
      localStorage.removeItem('isFemale');
      localStorage.removeItem('showTutorial');
      localStorage.removeItem('tutorialStep');
      localStorage.removeItem('isMusicOn');
      
      // Reset state variables
      setAmountOfSongs(0);
      setSongScoreAvg(0);
      setPrevSongScoreAvg(0);
      
      // Optionally reset other state variables
      setShowWelcomeScreen(true);
      setIsFemale(false);
      setShowTutorial(isDev ? false : true);
      setTutorialStep(0);
      // For isMusicOn, you'd need to call the toggleMusic function if it's not already true
    } catch (error) {
      console.error('Error resetting data in localStorage:', error);
    }
  }, [isDev]);



  const showSettingsDialog = () => {
    setDialogContent('settings');
    setShowDialog(true);
  };

  const showingSettingsDialog = showDialog && dialogContent === 'settings';

  // Add a useEffect for each state variable to save to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('showWelcomeScreen', showWelcomeScreen.toString());
    } catch (error) {
      console.error('Error saving showWelcomeScreen to localStorage:', error);
    }
  }, [showWelcomeScreen]);

  useEffect(() => {
    try {
      localStorage.setItem('isFemale', isFemale.toString());
    } catch (error) {
      console.error('Error saving isFemale to localStorage:', error);
    }
  }, [isFemale]);

  useEffect(() => {
    try {
      localStorage.setItem('showTutorial', showTutorial.toString());
    } catch (error) {
      console.error('Error saving showTutorial to localStorage:', error);
    }
  }, [showTutorial]);

  useEffect(() => {
    try {
      localStorage.setItem('tutorialStep', tutorialStep.toString());
    } catch (error) {
      console.error('Error saving tutorialStep to localStorage:', error);
    }
  }, [tutorialStep]);

  return (
    <Router>
      <DialogModal
        show={showDialog}
        title={getDialogTitle()}
        onClose={() => {
          // Initialize or play audio when closing the dialog
          initializeOrPlay();
          
          setShowDialog(false)
          if (showWelcomeScreen) {
            setShowWelcomeScreen(false)
          }
          if (showConfirmResetStats) {
            setShowConfirmResetStats(null)
          }
        }}
        closeButtonText={showWelcomeScreen ? "Vibe" : "X"}
        showOverlay={!showingSettingsDialog}
        dialogClassName={showingSettingsDialog ? `bottom-dialog ${!showWelcomeScreen ? 'is-settings-only' : ''}` : ''}
      >
        {showWelcomeScreen && <h3>Settings</h3>}
        {renderDialogContent()}
      </DialogModal>

      <div className="App">
        <main className="container">
          <div className={`beats-container ${isInFloor ? 'is-in' : 'is-out'}`}
            style={{
              opacity: isGameActive ? 1 : 0
            }}
          >
            <Beats
            setWonAmount={setWonAmount} 
            flagToTriggerHandleHit={flagToTriggerHandleHit}
              isGameActive={isGameActive}
              toggleGameActive={toggleGameActive}
              isPreGameCountdown={isPreGameCountdown}
            />
          </div>

            <Game
              setIsInFloor={setIsInFloor}
              cornerIndexCurrent={cornerIndexCurrent}
              setCornerIndexCurrent={setCornerIndexCurrent}
              cornerIndexNext={cornerIndexNext}
              setCornerIndexNext={setCornerIndexNext}
              flagToTriggerHandleHit={flagToTriggerHandleHit}
              toggleAngleFlag={toggleAngleFlag}
              isFemale={isFemale}
              useShaders={!performanceSettings.isLowPerformanceDevice}
              showTutorial={showTutorial}
              hideJoystick={showTutorial && tutorialStep === 0 || showingSettingsDialog}
            />



          <div id="footer" style={{opacity: showingSettingsDialog ? 0 : 1}}>
            <div className={`score-row ${isGameActive ? 'score-row-now' : ''}`} {...tutorialStepZeroOpacity()}>
            <div
                className={`d-button scorer scorer-out ${isInFloor ? '' : 'now'}`}
              >
                <span>OUT{isGameActive ? <b>{wonAmountOutFloor}</b> : ''}</span>
              </div>
              <div style={{opacity: 0}}></div>

              <div
                className={`d-button scorer scorer-in ${isInFloor ? 'now' : ''}`}
              >
                <span>IN{isGameActive ? <b>{wonAmountInFloor}</b> : ''}</span>
              </div>
            </div>

            <div className="footer-row d-game-button-row">

              {showTutorial ? (     <div className={`d-button countdown-info tutorial-info tutorial-step-${tutorialStep}`}>
                  <span><b>{tutorialStep === 0 ? <>1. Start by dancing <strong>{tutorialDanceCount} times</strong></> : <>2. Move to the <strong>DJ's chosen floor</strong><br />to be <strong>IN</strong> and earn more dance points</>}</b></span>
                </div>) : (isPreGameCountdown ? (
                <div className="d-button countdown-info ready-countdown">
                  <span><b>Ready</b> {preGameCount > 0 ? preGameCount : "GO!"}</span>
                </div>
              ) : !isGameActive ? (
                <button
                  className={`d-button toggle-button ${isGameActive ? '' : 'cta-button'} ${tutorialStep == 2 ? 'ready-to' : ''}`}
                  onClick={toggleGameActive}
                >
                  <span>{isGameActive ? 'Stop Game' : 'Start Song'}</span>
                </button>
              ) : (
                <div className="d-button countdown-info">
                  <span><b>Ends in</b>{countdown.toFixed(1)}s</span>
                </div>
              ))}

            </div>
            <div className="footer-row">
              <div className="move-info d-button" {...tutorialStepZeroOpacity()}>
                <span>Move<b>W</b><b>A</b><b>S</b><b>D</b></span>
              </div>
            <button 
                  {...tutorialStepZeroOpacity(1)}
                className={`d-button dance-button ${isGameActive || (showTutorial && tutorialStep === 0) ? 'cta-button' : ''}`}
              onClick={triggerHandleHit}
              onTouchEnd={(e) => {
                e.preventDefault(); // Prevent default touch behavior
                triggerHandleHit(); // Call the function on touch end
              }}
            >
              <span>Dance</span>
            </button>
            </div>
            <div className="footer-row d-settings-button-row">


              <button
                  className={`d-button d-small-button ${isFemale ? 'is-female' : ''}`}
                  onClick={showSettingsDialog}
              >
                <span>⚙️</span>
              </button>
              <div className="d-button countdown-info"></div>

              <button
                  className="d-button d-small-button"
                  style={{
                    opacity: 0
                  }}
              >
                <span>⚙️</span>
              </button>
            </div>

            <div className="footer-row">
              <img id="logo" src="logo.svg" alt="logo" />
            </div>
          </div>
        </main>
      </div>


      <a target="_blank" href="https://jam.pieter.com" className="vibe-jam-link">🕹️ <br/> Vibe <br/> Jam 2025</a>
      <button
          className="d-button audio-init-button"
          onClick={initializeOrPlay}
          style={{
            display : 'none'
          }}
        >
          <span>Enable Audio</span>
        </button>

    </Router>
  );
}

export default App;

